import moment from 'moment-timezone'
import { isUnassignedVehicle, resolveWindowInputs } from './orderWindow'

/**
 * Builds the `fields` patch the optimizer's publish (publishRoutes) writes
 * back to a TMS_ORDERS doc with `merge: true`.
 *
 * Pure on purpose: it must stay easy to assert what the save does and — just as
 * importantly — what it must NEVER touch. Three invariants ride on this:
 *  - it never writes appointment fields (AppointmentStartTime/EndTime/HasAppointment),
 *    so an existing appointment window is preserved across a save;
 *  - it never writes driver-push fields (DriverPushStatus/DriverPushTime/LastDriverSync),
 *    so a merge can't clobber a "Pushed" status set by pushToDrivers; and
 *  - it never moves OrderStatus BACKWARDS onto an order already pushed to a driver
 *    (see `alreadyPushed`).
 *
 * Ported from Vue OPTIMIZER src/utils/orderUpdateFields.ts (2026-06-18).
 */

export interface SaveOrderLike {
  vehicle: string
  serviceTime: number
  isTruckLocked?: boolean
  timeLocked?: boolean
  timeZone: string
  startWindow: string
  endWindow: string
  pickupDelivery?: string
  address?: string
}

export interface VehicleDriverLike {
  firstName?: string
  lastName?: string
  email?: string
}

export interface BuildOrderUpdateFieldsParams {
  order: SaveOrderLike
  selectedDate: string
  /** The order's current `fields.OpsStatuses` (preserved minus the transient ones). */
  existingOpsStatuses?: any[]
  /** GLOBALDEF ops-status value -> doc id, used to stamp added statuses. */
  opsStatusIdMap?: Map<string, string>
  /** The vehicle's resolved driver (vehicle.driver) for `order.vehicle`. */
  vehicleDriver?: VehicleDriverLike | null
  /** updateAll || (non-DropOff order whose vehicle is in the save set). */
  includeVehicleDriver: boolean
  /** updateAll || the order's vehicle is in the address-update set. */
  includeAddress: boolean
  /**
   * The driver app already holds this order. Suppresses the OrderStatus /
   * AutoBillLatestAction stamps so a re-publish can't roll a live order back to
   * "Ready To Dispatch".
   *
   * Computed by the caller (optimizerSlice `isAlreadyPushed`) from BOTH
   * `fields.DriverPushStatus === 'Pushed'` AND the order's current lifecycle:
   * pushed alone is a one-way flag nothing ever clears, so an order sent back to
   * Order Received stayed suppressed and could never advance again.
   *
   * Note the flag is still needed alongside the lifecycle: pushToDrivers writes
   * OrderStatus "Not Started", and so does plain vehicle assignment on the
   * dispatch board (dashboardSlice transitionOrder) — both sit in "At Dispatching
   * Location", so the lifecycle alone can't tell them apart either.
   */
  alreadyPushed?: boolean
}

export function buildOrderUpdateFields({
  order,
  selectedDate,
  existingOpsStatuses = [],
  opsStatusIdMap = new Map<string, string>(),
  vehicleDriver = null,
  includeVehicleDriver,
  includeAddress,
  alreadyPushed = false,
}: BuildOrderUpdateFieldsParams): Record<string, any> {
  const unassignedVehicle = isUnassignedVehicle(order.vehicle)

  // Preserve the order's current statuses (minus the transient "Min Srvc Time"
  // ones), then toggle the optimizer-driven statuses on/off.
  const opsStatus = new Map<string, any>(
    (existingOpsStatuses || [])
      .filter((status: any) => !status?.Status?.includes?.('Min Srvc Time'))
      .map((status: any) => [status.Status || status, status.id || status]),
  )

  const statusUpdates: Record<string, boolean> = {
    [`${order.serviceTime} Min Srvc Time`]: !unassignedVehicle,
    OPTIMIZED: !unassignedVehicle,
    'Vehicle Locked': !!order.isTruckLocked,
    'Time Locked': !!order.timeLocked,
  }
  Object.entries(statusUpdates).forEach(([status, shouldExist]) => {
    if (shouldExist) {
      opsStatus.set(status, { Id: opsStatusIdMap.get(status) || status, Status: status })
    } else {
      opsStatus.delete(status)
    }
  })

  const { startInput, endInput } = resolveWindowInputs(order, selectedDate)
  const fields: Record<string, any> = {
    StartWindow: moment.tz(startInput, order.timeZone).toDate(),
    EndWindow: moment.tz(endInput, order.timeZone).toDate(),
    OpsStatuses: Array.from(opsStatus.values()),
  }

  // Orders sitting in an unassigned bucket (Unfit Stops / ReadyToRoute /
  // PreRoute) are not optimized output, so don't stamp the "Optimized" marker on
  // them. Assigned vehicles get it alongside the OPTIMIZED ops status above.
  // An already-pushed order keeps its "Pushed to Driver" marker: the OPTIMIZED
  // ops status above already records that the re-publish happened.
  if (!unassignedVehicle && !alreadyPushed) {
    fields.AutoBillLatestAction = 'Optimized'
  }

  if (includeVehicleDriver) {
    let driverValue = ''
    if (
      !unassignedVehicle &&
      order.vehicle &&
      vehicleDriver &&
      vehicleDriver.email &&
      vehicleDriver.firstName &&
      vehicleDriver.lastName
    ) {
      const fullName = `${vehicleDriver.firstName} ${vehicleDriver.lastName}`.trim()
      driverValue = `${fullName}***${vehicleDriver.email}`
    }
    fields.Vehicle = unassignedVehicle ? '' : order.vehicle
    fields.Driver = driverValue
    // Advance an assigned order to Ready To Dispatch on publish. This is keyed on
    // vehicle assignment alone (NOT on a resolved driver) — a route's vehicle can
    // be assigned before a driver is, and the order is still ready to dispatch.
    // (Diverges from Vue OPTIMIZER, which additionally gated this on driverValue.)
    //
    // Only ADVANCE — never roll an order back onto it. Once pushToDrivers has moved
    // the order to "Not Started" the driver app holds it, and nothing downstream
    // would undo the rollback: handleUpdates deliberately strips OrderStatus from
    // its merge (it doesn't compute the field), so a stale "Ready To Dispatch"
    // written here would simply stick — hence the alreadyPushed guard.
    if (!unassignedVehicle && !alreadyPushed) {
      fields.OrderStatus = 'Ready To Dispatch'
    }
  }

  if (includeAddress) {
    const [apt, stDr, unit, city, postal, province, country] = (order.address || '').split('***')
    fields.DropoffApt = apt || ''
    fields.DropoffStDr = stDr || ''
    fields.DropoffUnit = unit || ''
    fields.DropoffCityTown = city || ''
    fields.DropoffPostalCode = postal || ''
    fields.DropOffProvince = province || ''
    fields.DropoffCountry = country || ''
  }

  return fields
}
